import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Copy, Mail, Check, Send } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { emailSignature } from "../mock";
import { emailApi } from "../api";

const templates = {
  followup: (name) => ({
    subject: "Following up on your home care request — Caregivers Alliance",
    body: `Hi ${name || "there"},\n\nThank you for reaching out to Caregivers Alliance Homecare. I'd love to learn more about your home care needs and answer any questions.\n\nWhen is a good time for a quick call? You can also reach us anytime at (614) 526-1548.\n\nWarm regards,\n\n${emailSignature}`,
  }),
  referral: (name) => ({
    subject: "Home care referral — Caregivers Alliance Homecare",
    body: `Hello,\n\nThank you for referring ${name || "your patient"} to Caregivers Alliance Homecare. Our intake team is ready to begin the assessment and coordinate a safe transition home.\n\nPlease let us know the discharge date and any specific care needs.\n\nWith gratitude,\n\n${emailSignature}`,
  }),
  welcome: (name) => ({
    subject: "Welcome to Caregivers Alliance Homecare",
    body: `Dear ${name || "valued client"},\n\nWelcome to the Caregivers Alliance family! We're honored to care for your loved one. Your dedicated care coordinator will be in touch to finalize the care plan.\n\nWarm regards,\n\n${emailSignature}`,
  }),
};

const EmailModal = ({ open, onOpenChange, lead }) => {
  const { toast } = useToast();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [to, setTo] = useState("");

  useEffect(() => {
    if (lead) {
      const t = templates.followup(lead.name);
      setSubject(t.subject);
      setBody(t.body);
      setTo(lead.email || (lead.contact && lead.contact.includes("@") ? lead.contact : ""));
    }
  }, [lead]);

  if (!lead) return null;

  const applyTemplate = (key) => {
    const t = templates[key](lead.name);
    setSubject(t.subject);
    setBody(t.body);
  };

  const emailAddr = to;

  const sendNow = async () => {
    if (!emailAddr) {
      toast({ title: "Add a recipient email", variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      await emailApi.send({ recipient_email: emailAddr, subject, body, lead_id: lead.id });
      toast({ title: "Email sent!", description: `Delivered to ${emailAddr}.` });
      onOpenChange(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send. Check the address or your Resend setup.";
      toast({ title: "Send failed", description: msg, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const copyAll = async () => {
    const text = `Subject: ${subject}\n\n${body}`;
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        ok = true;
      }
    } catch (e) { /* fall through */ }
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (e) { ok = false; }
    }
    setCopied(ok);
    toast({
      title: ok ? "Copied" : "Copy failed",
      description: ok ? "Email copied to clipboard." : "Please select the text and copy manually.",
    });
    setTimeout(() => setCopied(false), 1500);
  };

  const openMail = () => {
    const url = `mailto:${emailAddr}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(url, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Compose email to {lead.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => applyTemplate("followup")}>Follow-up</Button>
            <Button size="sm" variant="outline" onClick={() => applyTemplate("referral")}>Referral</Button>
            <Button size="sm" variant="outline" onClick={() => applyTemplate("welcome")}>Welcome</Button>
          </div>
          <div>
            <Label>To</Label>
            <Input className="mt-1" value={to} onChange={(e) => setTo(e.target.value)} placeholder="recipient@email.com" />
          </div>
          <div>
            <Label>Subject</Label>
            <Input className="mt-1" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div>
            <Label>Message</Label>
            <Textarea className="mt-1" rows={8} value={body} onChange={(e) => setBody(e.target.value)} />
          </div>
          <div className="flex flex-wrap gap-3">
            <Button onClick={copyAll} variant="outline" className="flex-1 min-w-[110px]">
              {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />} Copy
            </Button>
            <Button onClick={openMail} disabled={!emailAddr} variant="outline"
              className="flex-1 min-w-[110px]">
              <Mail className="mr-2 h-4 w-4" /> Mail app
            </Button>
            <Button onClick={sendNow} disabled={!emailAddr || sending}
              className="flex-1 min-w-[110px] bg-[#2E9E6A] hover:bg-[#23875A] text-white">
              <Send className="mr-2 h-4 w-4" /> {sending ? "Sending..." : "Send"}
            </Button>
          </div>
          <p className="text-xs text-slate-400 text-center">
            "Send" delivers instantly via Resend. In test mode Resend only delivers to your verified address — verify myohcare.com in Resend to email anyone.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmailModal;
